import { AcrCategory, AcrProbability, AcrSeverity, AcrStatus, type StudyAcrEntrySelectDto } from '@@types/api.types';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useCreateMeiaAcrEntry, useUpdateMeiaAcrEntry } from '@queries/meia.query';
import { type MeiaAcrFormValues, meiaAcrSchema } from '@schemas/meia.schema';
import { useStore } from '@tanstack/react-form';
import { createFormInvalidHandler } from '@utils/form.util';
import { type FC, useEffect } from 'react';
import { MEIA_ACR_ENTRY_FORM_ID, getAcrImpactLabel } from './meia-acr.util';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';

type MeiaAcrEntryFormProps = {
  entry?: StudyAcrEntrySelectDto;
  onClose: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  onPendingChange: (isPending: boolean) => void;
  studyId: string;
};

const CATEGORY_OPTIONS = [
  { label: 'Assumption', value: AcrCategory.ASSUMPTION },
  { label: 'Constraint', value: AcrCategory.CONSTRAINT },
  { label: 'Risk', value: AcrCategory.RISK },
];

const STATUS_OPTIONS = [
  { label: 'Open', value: AcrStatus.OPEN },
  { label: 'Mitigated', value: AcrStatus.MITIGATED },
  { label: 'Accepted', value: AcrStatus.ACCEPTED },
  { label: 'Closed', value: AcrStatus.CLOSED },
];

const LIKELIHOOD_OPTIONS = [
  { label: 'Very Low', value: AcrProbability.VERY_LOW },
  { label: 'Low', value: AcrProbability.LOW },
  { label: 'Medium', value: AcrProbability.MEDIUM },
  { label: 'High', value: AcrProbability.HIGH },
  { label: 'Very High', value: AcrProbability.VERY_HIGH },
];

const IMPACT_OPTIONS = [
  { label: 'Low', value: AcrSeverity.LOW },
  { label: 'Medium', value: AcrSeverity.MEDIUM },
  { label: 'High', value: AcrSeverity.HIGH },
  { label: 'Critical', value: AcrSeverity.CRITICAL },
];

const IMPACT_PLACEHOLDERS: Readonly<Record<AcrCategory, string>> = {
  [AcrCategory.ASSUMPTION]: 'What goes wrong if this assumption proves false.',
  [AcrCategory.CONSTRAINT]: 'How this constraint limits or shapes operations.',
  [AcrCategory.RISK]: '',
};

const buildDefaults = (entry?: StudyAcrEntrySelectDto): MeiaAcrFormValues => ({
  category: entry?.category ?? AcrCategory.ASSUMPTION,
  impact: entry?.impact ?? null,
  likelihood: entry?.likelihood ?? null,
  mitigationApproach: entry?.mitigationApproach ?? '',
  potentialImpact: entry?.potentialImpact ?? '',
  rationale: entry?.rationale ?? '',
  statement: entry?.statement ?? '',
  status: entry?.status ?? AcrStatus.OPEN,
  title: entry?.title ?? '',
});

export const MeiaAcrEntryForm: FC<MeiaAcrEntryFormProps> = (props) => {
  const { entry, onClose, onDirtyChange, onPendingChange, studyId } = props;
  const isEdit = Boolean(entry);

  const { isPending: isCreating, mutateAsync: createEntry } = useCreateMeiaAcrEntry();
  const { isPending: isUpdating, mutateAsync: updateEntry } = useUpdateMeiaAcrEntry();
  const isPending = isCreating || isUpdating;

  useEffect(() => {
    onPendingChange(isPending);
  }, [isPending, onPendingChange]);

  const form = useAppForm({
    defaultValues: buildDefaults(entry),
    onSubmit: async ({ value }) => {
      const validation = meiaAcrSchema.safeParse(value);
      if (!validation.success) {
        errorToast({ description: 'Please complete every required field.' });
        return;
      }

      const { category, impact, likelihood, mitigationApproach, potentialImpact, rationale, statement, status, title } =
        validation.data;
      const isRisk = category === AcrCategory.RISK;
      const shared = {
        category,
        impact: isRisk ? impact : null,
        likelihood: isRisk ? likelihood : null,
        mitigationApproach: mitigationApproach.trim() ? mitigationApproach.trim() : null,
        potentialImpact: isRisk || !potentialImpact.trim() ? null : potentialImpact.trim(),
        rationale,
        statement,
        status,
        title,
      };

      try {
        if (entry) {
          await updateEntry({ acrEntryId: entry.id, studyId, ...shared });
        } else {
          await createEntry({ studyId, ...shared });
        }
        successToast({ description: isEdit ? 'ACR entry updated.' : 'ACR entry created.' });
        onClose();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save ACR entry.';
        errorToast({ description: message });
      }
    },
    onSubmitInvalid: createFormInvalidHandler<MeiaAcrFormValues>(),
    validators: { onSubmit: meiaAcrSchema },
  });

  const isDirty = useStore(form.store, (state) => state.isDirty);
  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <form
      {...MEIA_AUTOFILL_IGNORE_PROPS}
      className="flex flex-col gap-4"
      id={MEIA_ACR_ENTRY_FORM_ID}
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <form.AppField name="category">
            {(field) => (
              <field.SelectField
                label="Category"
                options={CATEGORY_OPTIONS}
                required
              />
            )}
          </form.AppField>
          <form.AppField name="status">
            {(field) => (
              <field.SelectField
                label="Status"
                options={STATUS_OPTIONS}
                required
              />
            )}
          </form.AppField>
        </div>
        <form.AppField name="title">
          {(field) => (
            <field.TextField
              label="Title"
              placeholder="e.g., Coalition C2 availability"
              required
            />
          )}
        </form.AppField>
        <form.AppField name="statement">
          {(field) => (
            <field.TextAreaField
              label="Statement"
              placeholder="e.g., Adversary IADS stays at current readiness through H-hour."
              required
              rows={2}
            />
          )}
        </form.AppField>
        <form.AppField name="rationale">
          {(field) => (
            <field.TextAreaField
              label="Rationale"
              placeholder="Why this assumption / constraint / risk holds."
              required
              rows={2}
            />
          )}
        </form.AppField>
        <form.Subscribe selector={(state) => state.values.category}>
          {(category) =>
            category === AcrCategory.RISK ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <form.AppField name="likelihood">
                  {(field) => (
                    <field.SelectField
                      clearable
                      label="Likelihood"
                      options={LIKELIHOOD_OPTIONS}
                      placeholder="Not set"
                    />
                  )}
                </form.AppField>
                <form.AppField name="impact">
                  {(field) => (
                    <field.SelectField
                      clearable
                      label="Impact"
                      options={IMPACT_OPTIONS}
                      placeholder="Not set"
                    />
                  )}
                </form.AppField>
              </div>
            ) : (
              <form.AppField name="potentialImpact">
                {(field) => (
                  <field.TextAreaField
                    label={getAcrImpactLabel(category)}
                    placeholder={IMPACT_PLACEHOLDERS[category]}
                    rows={2}
                  />
                )}
              </form.AppField>
            )
          }
        </form.Subscribe>
        <form.AppField name="mitigationApproach">
          {(field) => (
            <field.TextAreaField
              label="Mitigation"
              placeholder="How the risk is reduced (required to clear an open risk at sign-off)."
              rows={2}
            />
          )}
        </form.AppField>
      </form.AppForm>
    </form>
  );
};
